// Shared handling for the backend refusing a Stripe checkout because the
// signed-in user is an active organisation member (their plan comes from the
// organisation, so it cannot be bought individually).
//
// The backend signals this with `code: 'org_member_cannot_purchase'` on:
//   - POST /api/stripe/create-checkout-session -> HTTP 403
//   - POST /api/stripe/user-subscription       -> HTTP 409 (return page)
//
// Callers previously treated any response without a checkout URL as "not
// signed in" and sent the user to /login with pending checkout keys set, which
// Login.js / continueAfterSignIn.js then retried. For a signed-in member that
// loops, so every caller checks for this refusal first.

export const ORG_MEMBER_CANNOT_PURCHASE = 'org_member_cannot_purchase';

export const ORG_MEMBER_FALLBACK_MESSAGE =
  "Your plan comes from your organisation, so it can't be bought here while you are a member.";

const REFUSAL_STATUSES = [403, 409];

// Refusals the buyer can act on. Each shows the server's message; a missing
// message falls back to a generic line.
export const ALREADY_OWNED = 'already_owned';
const PURCHASE_REFUSAL_CODES = [ALREADY_OWNED, 'subject_required', 'subject_unknown'];

export const PURCHASE_REFUSAL_FALLBACK_MESSAGE = 'This purchase could not be started. Nothing was charged.';

const PENDING_CHECKOUT_KEYS = [
  'pending_checkout_tier',
  'pending_checkout_exam',
  'pending_checkout_package',
  'pending_checkout_subject',
];

/**
 * Returns the user-facing message when a checkout response is the
 * organisation-member refusal, otherwise null.
 *
 * @param {Response|{status?: number}|null|undefined} res
 * @param {unknown} data parsed JSON body
 * @returns {string|null}
 */
export function checkoutRefusalMessage(res, data) {
  if (!data || typeof data !== 'object') return null;
  const isOrg = data.code === ORG_MEMBER_CANNOT_PURCHASE;
  if (!isOrg && !PURCHASE_REFUSAL_CODES.includes(data.code)) return null;
  const status = res && typeof res === 'object' ? res.status : undefined;
  if (!REFUSAL_STATUSES.includes(status) && !(isOrg === false && status === 400)) return null;
  if (typeof data.error === 'string' && data.error.trim() !== '') {
    return data.error;
  }
  return isOrg ? ORG_MEMBER_FALLBACK_MESSAGE : PURCHASE_REFUSAL_FALLBACK_MESSAGE;
}

/**
 * Removes the pending checkout keys so Login / continueAfterSignIn do not
 * retry a checkout the server has refused.
 */
export function clearPendingCheckout() {
  PENDING_CHECKOUT_KEYS.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (err) {
      // Storage can be unavailable (private mode, blocked site data); there
      // is nothing pending to clear in that case.
    }
  });
}

/**
 * Clears pending checkout state and tells the user why the purchase was
 * refused.
 *
 * @param {string} message
 */
export function showCheckoutRefusal(message) {
  clearPendingCheckout();
  window.alert(message);
}
